<template>
  <div>
    <v-dialog v-model="dialog" max-width="1200px" scrollable fullscreen>
      <v-card>
        <v-card-title
          class="primary white--text d-flex justify-space-between align-center px-6 py-4"
        >
          <div class="d-flex align-center">
            <v-icon class="mr-3 white--text">mdi-calendar-edit</v-icon>
            <span class="text-h6">{{ titleForm }}</span>
          </div>
          <v-btn icon small @click="close">
            <v-icon class="white--text">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-6">
          <div v-if="isLoading">
            <v-row>
              <v-col cols="12" md="6">
                <v-card outlined class="mb-6">
                  <v-card-title class="subtitle-1 grey lighten-5 py-2">
                    <v-skeleton-loader type="text" width="150" />
                  </v-card-title>
                  <v-card-text class="pt-4 pa-5">
                    <v-skeleton-loader
                      v-for="n in 3"
                      :key="n"
                      type="list-item-avatar"
                      class="mb-4"
                    />
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" md="6">
                <v-card outlined class="mb-6">
                  <v-card-title class="subtitle-1 grey lighten-5 py-2">
                    <v-skeleton-loader type="text" width="150" />
                  </v-card-title>
                  <v-card-text class="pt-4 pa-5">
                    <v-skeleton-loader
                      v-for="n in 2"
                      :key="`time-${n}`"
                      type="list-item-avatar"
                      class="mb-4"
                    />
                    <v-skeleton-loader type="article" class="mt-4" />
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12">
                <v-card outlined>
                  <v-card-title class="subtitle-1 grey lighten-5 py-2">
                    <v-skeleton-loader type="text" width="150" />
                  </v-card-title>
                  <v-card-text class="pt-4 pa-5">
                    <v-skeleton-loader type="paragraph" />
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </div>

          <v-form ref="form" lazy-validation v-else>
            <v-row>
              <v-col cols="12" md="6">
                <v-card outlined class="mb-6">
                  <v-card-title class="subtitle-1 grey lighten-5 py-2">
                    <v-icon left small>mdi-information</v-icon>
                    Events
                  </v-card-title>
                  <v-card-text class="pt-4 pa-5 pa-5">
                    <v-select
                      v-model="formData.eventFor"
                      :items="eventFor"
                      label="Event For"
                      :rules="requiredRules"
                      dense
                      class="mb-4"
                      filled
                    />
                    <v-autocomplete
                      v-model="formData.event"
                      :items="
                      formData.eventFor === 'Client'
                        ? clientEvents
                        : staffEvents
                    "
                      label="Event Type"
                      dense
                      class="mb-4"
                      @change="visit"
                      filled
                    />

                    <v-select
                      v-model="formData.client"
                      :items="clients"
                      item-text="Name"
                      item-value="_id"
                      label="Select Client"
                      :rules="requiredRules"
                      filled
                      dense
                      @change="getProviders"
                    />
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" md="6">
                <v-card outlined class="mb-6">
                  <v-card-title class="subtitle-1 grey lighten-5 py-2">
                    <v-icon left small>mdi-clock-outline</v-icon>
                    Schedule
                  </v-card-title>

                  <v-card-text class="pt-4 pa-5">
                    <v-row>
                      <v-col cols="12" sm="6">
                        <v-datetime-picker
                          v-model="formData.startDate"
                          label="Start Date & Time"
                          time-format="hh:mm a"
                          date-format="MM/dd/yyyy"
                          :rules="[(v) => !!v || 'Required']"
                          filled
                          dense
                          class="mb-4"
                        />
                      </v-col>

                      <v-col cols="12" sm="6">
                        <v-datetime-picker
                          v-model="formData.endDate"
                          label="End Date & Time"
                          time-format="hh:mm a"
                          date-format="MM/dd/yyyy"
                          :rules="requiredRules"
                          filled
                          dense
                        />
                      </v-col>
                    </v-row>

                    <v-divider class="my-4" />

                    <div>
                      <div class="caption grey--text mb-2">
                        Is this a recurring event?
                      </div>
                      <v-radio-group
                        v-model="formData.isRecurringEvent"
                        row
                        class="mt-0"
                      >
                        <v-radio label="Yes" value="Yes" />
                        <v-radio label="No" value="No" />
                      </v-radio-group>

                      <v-expand-transition>
                        <v-select
                          v-if="formData.isRecurringEvent === 'Yes'"
                          v-model="formData.selectedOption"
                          :items="optionsForRecurring"
                          label="Recurring Pattern"
                          :rules="
                          formData.isRecurringEvent === 'Yes'
                            ? requiredRules
                            : []
                        "
                          filled
                          dense
                          class="mt-4"
                        />
                      </v-expand-transition>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col v-if="drsVisit" cols="12">
                <v-card outlined class="mb-6">
                  <v-card-title class="subtitle-1 grey lighten-5 py-2">
                    <v-icon left small>mdi-doctor</v-icon>
                    Doctor's Visit Details
                  </v-card-title>

                  <v-card-text class="pt-4 pa-5">
                    <v-row>
                      <v-col cols="12" sm="6">
                        <v-select
                          v-model="formData.reviewingProvider"
                          label="Providers"
                          :items="providers"
                          item-text="Provider.Name"
                          item-value="Provider._id"
                          :rules="requiredRules"
                          filled
                          dense
                        />
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-select
                          v-model="formData.visitType"
                          label="Visit Type"
                          :items="visitTypes"
                          :rules="requiredRules"
                          filled
                          dense
                        />
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12">
                <v-card outlined class="mb-6">
                  <v-card-title class="subtitle-1 grey lighten-5 py-2">
                    <v-icon left small>mdi-account-group</v-icon>
                    Staff Assignment
                  </v-card-title>

                  <v-card-text class="pt-4 pa-5">
                    <v-row>
                      <v-col cols="12" sm="6">
                        <v-autocomplete
                          v-model="formData.staff"
                          :items="users"
                          item-text="Name"
                          item-value="_id"
                          label="Staff Assigned"
                          :rules="requiredRules"
                          filled
                          dense
                          clearable
                          return-object
                          :filter="customFilter"
                        >
                          <template #selection="{ item }">
                            <v-chip
                              v-if="item"
                              small
                              color="primary"
                              text-color="white"
                            >
                              {{ item.Name }}
                            </v-chip>
                          </template>
                        </v-autocomplete>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-select
                          v-model="formData.program"
                          :items="selectedClientPrograms"
                          item-text="Name"
                          label="Program"
                          :rules="requiredRules"
                          filled
                          dense
                        />
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col v-if="formData.eventFor === 'Client'" cols="12">
                <v-card outlined class="mb-6">
                  <v-card-title class="subtitle-1 grey lighten-5 py-2">
                    <v-icon left small>mdi-car</v-icon>
                    Transportation
                  </v-card-title>

                  <v-card-text class="pt-4 pa-5">
                    <div class="caption grey--text mb-2">
                      Is Transportation Required?
                    </div>
                    <v-radio-group
                      v-model="formData.transportation"
                      row
                      class="mt-0"
                    >
                      <v-radio
                        v-for="option in transportationOptions"
                        :key="option"
                        :label="option"
                        :value="option"
                      />
                    </v-radio-group>

                    <v-expand-transition>
                      <v-row v-if="formData.transportation === 'Yes'">
                        <v-col cols="12" sm="6">
                          <v-text-field
                            v-model="formData.from"
                            label="From Location"
                            filled
                            dense
                            :rules="
                            formData.transportation === 'Yes'
                              ? requiredRules
                              : []
                          "
                          />
                        </v-col>
                        <v-col cols="12" sm="6">
                          <v-text-field
                            v-model="formData.to"
                            label="To Location"
                            filled
                            dense
                            :rules="
                            formData.transportation === 'Yes'
                              ? requiredRules
                              : []
                          "
                          />
                        </v-col>
                      </v-row>
                    </v-expand-transition>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12">
                <v-card outlined class="mb-6">
                  <v-card-title class="subtitle-1 grey lighten-5 py-2">
                    <v-icon left small>mdi-text-box-outline</v-icon>
                    Additional Details
                  </v-card-title>

                  <v-card-text class="pt-4 pa-5">
                    <v-textarea
                      v-model="formData.notes"
                      label="Event Notes"
                      filled
                      auto-grow
                      rows="4"
                    />
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12">
                <v-card outlined>
                  <v-card-title class="subtitle-1 grey lighten-5 py-2">
                    <v-icon left small>mdi-information-outline</v-icon>
                    Created Information
                  </v-card-title>

                  <v-card-text class="pt-4 pa-5">
                    <v-row>
                      <v-col cols="12" sm="6">
                        <v-text-field
                          v-model="formData.dateCreated"
                          label="Date Created"
                          filled
                          dense
                          readonly
                        />
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-text-field
                          v-model="formData.createdBy"
                          label="Created By"
                          filled
                          dense
                          readonly
                        />
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider />
        <v-card-actions class="pa-6">
          <v-btn
            v-if="isEditMode"
            class="red darken-2 white--text"
            @click="showCancelModal(formData)"
            :loading="loading"
            :disabled="loading"
          >
            Cancel
          </v-btn>
          <v-spacer />
          <v-btn text @click="close" class="px-6"> Close </v-btn>
          <v-btn
            :color="isEditMode ? 'primary' : 'success'"
            :loading="loading"
            :disabled="loading"
            @click="save"
            class="px-6"
          >
            {{ isEditMode ? "Update" : "Save" }} and Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showModal" persistent max-width="500px">
      <v-card>
        <v-card-title>Cancel Event</v-card-title>
        <v-card-text>
          <v-textarea
            v-model="cancellationNote"
            placeholder="Enter cancellation reason (min 10 characters)"
            rows="4"
            cols="50"
          />
          <p v-if="cancellationNote && cancellationNote.length < 10">
            Note must be at least 10 characters (currently: {{ cancellationNote.length }})
          </p>
        </v-card-text>
        <v-card-actions>
          <v-btn :disabled="cancellationNote.length < 10" @click="eventCancel">
            Confirm
          </v-btn>
          <v-btn @click="closeModal">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import { mapActions } from "vuex";
import axios from "axios";
import moment from "moment";
const url = require("../../../client_config/config").url;

import {
  CLIENT_EVENTS,
  STAFF_EVENTS,
  VISIT_TYPES,
  EVENT_FOR,
  RECURRING_OPTIONS,
  TRANSPORTATION_OPTIONS,
} from "@/constants/calendarConstants";
import toastr from "toastr";

export default {
  name: "CalendarEventDialog",

  props: {
    value: Boolean,
    titleForm: String,
    programs: Array,
    clients: Array,
    users: Array,
    eventData: {
      type: Object,
      default: () => null,
    },
  },

  data: () => ({
    eventFor: EVENT_FOR,
    optionsForRecurring: RECURRING_OPTIONS,
    transportationOptions: TRANSPORTATION_OPTIONS,
    dialog: false,
    loading: false,
    drsVisit: false,
    providers: [],
    selectedClientPrograms: [],
    requiredRules: [(v) => !!v || "Required"],
    search: "",
    formData: {
      eventFor: "",
      event: "",
      client: "",
      staff: "",
      program: "",
      startDate: "",
      endDate: "",
      notes: "",
      isRecurringEvent: "No",
      selectedOption: "",
      transportation: "No",
      from: "",
      to: "",
      dateCreated: new Date(),
      createdBy: localStorage.getItem("Name"),
      reviewingProvider: "",
      visitType: "",
    },
    clientEvents: CLIENT_EVENTS,
    staffEvents: STAFF_EVENTS,
    visitTypes: VISIT_TYPES,
    isShowing: false,
    eventId: null,
    showModal: false,
    cancellationNote: '',
    selectedItem: null,
  }),

  created() {
    this.initializeDialog();
  },
  computed: {
    eventItems() {
      return this.formData.eventFor === "Client" ? CLIENT_EVENTS : STAFF_EVENTS;
    },

    availableVisitTypes() {
      return this.drsVisit ? VISIT_TYPES : [];
    },

    isEditMode() {
      return !!this.eventData;
    },

    isLoading() {
      return !this.isShowing && this.dialog;
    },
  },

  methods: {
    ...mapActions({
      getClientProviders: "clientProviders/getItem",
    }),

    async initializeDialog() {
      this.isShowing = false;
      if (this.eventData) {
        await this.$nextTick();
        await this.fillFormWithEventData();
      } else {
        this.resetForm();
        this.isShowing = true;
      }
    },

    async getProviders(clientId) {
      try {
        const response = await this.getClientProviders(clientId);
        this.providers = response.data;
        this.setProgram(clientId);
      } catch (error) {
        console.error("Error fetching providers:", error);
      }
    },

    setProgram(clientId) {
      const client = this.clients.find((client) => client._id === clientId);
      if (client && client.Program && client.Program.length) {
        this.selectedClientPrograms = client.Program;
        this.formData.program = client.Program[0];
      }
    },

    visit() {
      this.drsVisit = this.formData.event === "Dr. Visit";
      if (!this.drsVisit) {
        this.formData.reviewingProvider = "";
        this.formData.visitType = "";
      }
    },

    customFilter(item, queryText) {
      const searchText = queryText.toLowerCase();
      const itemName = item.Name.toLowerCase();
      return itemName.indexOf(searchText) > -1;
    },

    validateForm() {
      if (!this.$refs.form.validate()) {
        return false;
      }

      if (!this.formData.startDate) {
        this.$emit("showError", "Please input start date.");
        return false;
      }

      if (!this.formData.endDate) {
        this.$emit("showError", "Please input end date.");
        return false;
      }

      return true;
    },

    async save() {
      if (!this.validateForm()) {
        return;
      }
      this.loading = true;
      try {
        const payload = this.preparePayload();

        this.eventId =
          this.eventData?.isRecurringEvent === "Yes"
            ? this.eventData.recurringEventId
            : this.eventData?.id;

        if (this.isEditMode) {
          await axios.put(`${url}/calendar/${this.eventId}`, payload);
          axios.get(`${url}/google/auth/event/${this.eventId}`);
          this.$emit("showSuccess", "Event has been updated successfully");
        } else {
          const response = await axios.post(`${url}/calendar`, payload);
          axios.get(`${url}/google/auth/event/${response.data._id}`);
          this.$emit("showSuccess", "Event has been created successfully");
        }

        this.close();
        this.$emit("update-events", {
          start: { date: moment().startOf("month").format("YYYY-MM-DD") },
          end: { date: moment().endOf("month").format("YYYY-MM-DD") },
        });
      } catch (error) {
        console.error("Error saving event:", error);
        this.$emit(
          "showError",
          error.response?.data?.msg || "Error saving event"
        );
      } finally {
        this.loading = false;
      }
    },


    showCancelModal() {

      this.selectedItem = this.eventData;

      console.log({selectedItem: this.selectedItem});

      this.cancellationNote = '';
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.selectedItem = null;
      this.cancellationNote = '';
    },

    async eventCancel() {
      try {
        if (!this.cancellationNote || this.cancellationNote.length < 10) {
          toastr.error('Cancellation note must be at least 10 characters long');
          return;
        }

        const updatedItem = {
          ...this.selectedItem,
          note: this.cancellationNote
        };

        const response = await axios.put(`${url}/calendar/cancel`, updatedItem);

        if (response.status === 200 && response.data && response.data.msg) {
          this.$emit("showSuccess", "Event deleted successfully");
          this.$emit("update-events", {
            start: { date: moment().startOf("month").format("YYYY-MM-DD") },
            end: { date: moment().endOf("month").format("YYYY-MM-DD") },
          });
          this.closeModal();
        } else {
          throw new Error('Unexpected response from server');
        }
      } catch (error) {
        console.error("Error deleting event:", error);
        this.$emit(
          "showError",
          error.response?.data?.msg || "Error deleting event"
        );
      }
    },


    async deleteCalendar() {
      try {
        this.loading = true;
        let UrlNotification = "Calendar";

        this.eventId =
          this.eventData?.isRecurringEvent === "Yes"
            ? this.eventData.recurringEventId
            : this.eventData?.id;

        await axios.delete(`${url}/google/auth/event/${this.eventId}`);
        await axios.delete(
          `${url}/calendar/${this.eventId}/${UrlNotification}`
        );
        this.$emit("showSuccess", "Event deleted successfully");
        this.close();
        this.$emit("update-events", {
          start: { date: moment().startOf("month").format("YYYY-MM-DD") },
          end: { date: moment().endOf("month").format("YYYY-MM-DD") },
        });
      } catch (error) {
        console.error("Error deleting event:", error);
        this.$emit(
          "showError",
          error.response?.data?.msg || "Error deleting event"
        );
      } finally {
        this.loading = false;
      }
    },

    preparePayload() {
      const payload = {
        Event: this.formData.event,
        EventFor: this.formData.eventFor,
        Client: this.formData.client,
        Staff: this.formData.staff,
        Program: this.formData.program,
        StartDate: this.formData.startDate,
        EndDate: this.formData.endDate,
        Notes: this.formData.notes,
        Transportation: this.formData.transportation,
        From: this.formData.from,
        To: this.formData.to,
        CreatedBy: localStorage.getItem("Name"),
        DateCreated: this.formData.dateCreated,
        ReviewingProvider: this.formData.reviewingProvider,
        VisitType: this.formData.visitType,
        UrlNotification: this.$router.history.current.path,
        isRecurringEvent: this.formData.isRecurringEvent,
        typeOfRecurringEvent: this.formData.selectedOption,
      };

      return payload;
    },

    close() {
      if (this.$refs.form) {
        this.$refs.form.reset();
      }

      this.formData = {
        eventFor: "",
        event: "",
        client: "",
        staff: "",
        program: "",
        startDate: "",
        endDate: "",
        notes: "",
        isRecurringEvent: "No",
        selectedOption: "",
        transportation: "No",
        from: "",
        to: "",
        dateCreated: new Date(),
        createdBy: localStorage.getItem("Name"),
        reviewingProvider: "",
        visitType: "",
      };

      this.drsVisit = false;
      this.providers = [];
      this.selectedClientPrograms = [];
      this.isShowing = false;

      this.dialog = false;
      this.$emit("close");
    },

    resetForm() {
      if (this.$refs.form) {
        this.$refs.form.reset();
      }

      this.formData = {
        eventFor: "",
        event: "",
        client: "",
        staff: "",
        program: "",
        startDate: "",
        endDate: "",
        notes: "",
        isRecurringEvent: "No",
        selectedOption: "",
        transportation: "No",
        from: "",
        to: "",
        dateCreated: new Date(),
        createdBy: localStorage.getItem("Name"),
        reviewingProvider: "",
        visitType: "",
      };

      this.drsVisit = false;
      this.providers = [];
      this.selectedClientPrograms = [];
      this.eventId = null;
    },

    searchProvider() {
      if (!this.search) {
        this.providers = this.clientProviders;
        return;
      }

      this.providers = this.clientProviders.filter((item) => {
        return item.Provider.Name.toLowerCase().includes(
          this.search.toLowerCase()
        );
      });
    },
    async fillFormWithEventData() {
      if (this.eventData) {
        const startDate = this.eventData.startDate
          ? new Date(this.eventData.startDate)
          : null;
        const endDate = this.eventData.endDate
          ? new Date(this.eventData.endDate)
          : null;

        this.formData = {
          eventFor: this.eventData.eventFor,
          event: this.eventData.event || "",
          client: this.eventData.client?._id || "",
          staff: this.eventData.staff?._id || "",
          program: this.eventData.program || "",
          startDate: startDate,
          endDate: endDate,
          notes: this.eventData.note || "",
          isRecurringEvent: this.eventData.isRecurringEvent || "No",
          selectedOption: this.eventData.selectedOption || "",
          transportation: this.eventData.transportation || "No",
          from: this.eventData.Tfrom || "",
          to: this.eventData.Tto || "",
          dateCreated: this.eventData.createdDate
            ? new Date(this.eventData.createdDate)
            : new Date(),
          createdBy: this.eventData.createdBy || localStorage.getItem("Name"),
          reviewingProvider: this.eventData.reviewingProvider || "",
          visitType: this.eventData.visitType || "",
        };

        if (this.formData.client) {
          await this.getProviders(this.formData.client);
          await this.setProgram(this.formData.client);
        }

        this.visit();
        this.isShowing = true;
      }
    },
  },
  watch: {
    value(val) {
      this.dialog = val;
      if (val) {
        this.initializeDialog();
      }
    },
    dialog(val) {
      this.$emit("input", val);
      if (!val) this.resetForm();
    },
  },
};
</script>

<style lang="scss" scoped>
@media (max-width: 600px) {
  .v-dialog {
    margin: 0;

    .v-card {
      display: flex;
      flex-direction: column;

      &__text {
        flex: 1;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
      }

      &__actions {
        position: sticky;
        bottom: 0;
        background: white;
        box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.1);
      }
    }
  }
}
</style>
